import React, { useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getBasicSupplier } from '../../../../redux/services/buyersService';
import dillLogoWithText from '../../../../assets/svg/dill_logo_with_text.svg';
import { ShieldCheck, ClockClockwise, Headset, IdentificationCard } from 'phosphor-react';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { DillButton } from '../../../general/Dill/DillButton';
import { AppRadioButton } from '../../../general/AppRadioButton/AppRadioButton';
import AppDatePicker from '../../../general/AppDatePicker/AppDatePicker';
import moment from 'moment';
import { getCreditApplicationTypeLabel, isExternalFormApplicationType } from '@dill/dill-shared';
import {
  getApplicationHistory,
  getCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { createCreditApplicationReference } from '../../../../redux/services/creditApplicationsReferenceService';
import { openDillToast } from '../../../../utils/helpers';
import JobSavingSuccess from '../../../../pages/RequestJobInformationSheet/JobSavingSuccess';
import { AppSubmitEmailModal } from '../../../general/AppSubmitEmailModal/AppSubmitEmailModal';

const BADGE_ICON_STYLE = { background: 'linear-gradient(135deg, #3762FB 0%, #1D85FF 100%)' };

const formatWithCommas = (raw: string, decimalPlaces = 2): string => {
  const clean = raw.replace(/[^0-9.]/g, '');
  if (decimalPlaces === 0) {
    const intPart = clean.split('.')[0];
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  const parts = clean.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (parts.length > 1) parts[1] = parts[1].slice(0, decimalPlaces);
  return parts.slice(0, 2).join('.');
};

const TrustBadge = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <div className="flex items-start gap-3 p-3 rounded-xl WHITE-BG shadow-sm border GREY_100-BORDER">
    <div
      className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full"
      style={BADGE_ICON_STYLE}>
      {icon}
    </div>
    <div>
      <p className="text-sm font-semibold GREY_900-CLR">{title}</p>
      <p className="text-xs GREY_500-CLR mt-0.5">{description}</p>
    </div>
  </div>
);

const SupportCard = () => (
  <div className="p-4 rounded-xl WHITE-BG shadow-sm border GREY_100-BORDER">
    <div className="flex items-center gap-2 mb-3">
      <div
        className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full"
        style={BADGE_ICON_STYLE}>
        <Headset size={18} className="text-white" weight="fill" />
      </div>
      <p className="text-sm font-semibold GREY_900-CLR">Need Help?</p>
    </div>
    <div className="space-y-1">
      <a
        href="mailto:support@usedill.com"
        className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
        <span className="GREY_500-CLR">Email:</span>
        <span className="PRIMARY_500-CLR font-medium">support@usedill.com</span>
      </a>
      <a
        href="tel:6283040931"
        className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
        <span className="GREY_500-CLR">Phone:</span>
        <span className="PRIMARY_500-CLR font-medium">(628) 304-0931</span>
      </a>
    </div>
  </div>
);

const INITIAL_STATE = {
  companyName: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  email: '',
  phoneNumber: '',
  openSince: '',
  termsOfPayment: '',
  totalBalance: '',
  currentDue: '',
  amountOfPastDue: '',
  highCredit: '',
  creditLimit: '',
  averageDaysBeyondTerms: '',
  terms30: '',
  terms60: '',
  terms90: '',
  terms120Plus: '',
  returnedChecks: '',
  liensFiled: '',
  badDebtWriteOffs: '',
  comments: '',
};
const REQUIRED_FIELDS = [
  'totalBalance',
  'currentDue',
  'openSince',
  'amountOfPastDue',
  'liensFiled',
  'badDebtWriteOffs',
];
enum ModalState {
  SEND_REFERENCE,
  SUCCESS_SENDING_REFERENCE,
}
export const CreditApplicationReference = () => {
  const dispatch = useAppDispatch();
  const [referenceInput, setReferenceInput] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [referenceId, setReferenceId] = useState('');
  const [referencePos, setReferencePos] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [submitEmailModal, setSubmitEmailModal] = useState(false);
  const [openSinceDate, setOpenSinceDate] = useState<Date | null>(null);

  const { creditApplication } = useAppSelector((state) => state.creditApplications);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [modalState, setModalState] = useState(ModalState.SEND_REFERENCE);
  const [modalDetails, setModalDetails] = useState<{
    modalType: 'Success' | 'Error';
    title: string;
    details: string;
  }>({
    modalType: 'Success',
    title: '',
    details: '',
  });
  const [hasErrors, setHasErrors] = useState(false);

  const { basicSupplier } = useAppSelector((state) => state.buyers);

  const onTextChange = (key: string, value: string) => {
    setReferenceInput((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
    setErrors((prev) => ({ ...prev, [key]: '' }));
    setHasErrors(false);
  };

  const fetchDataUsingTemplateId = async (id: string) => {
    const response = await dispatch(getCreditApplication({ creditApplicationId: id }));
    if (response.type === 'creditApplications/getCreditApplication/rejected') {
      await dispatch(getApplicationHistory({ creditApplicationId: id }));
    }
  };

  const fetchDataUsingDomainId = async (domainId: string) => {
    await dispatch(getBasicSupplier({ supplierDomainId: domainId }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramEmail = params.get('email');
    if (paramEmail) {
      setEmail(paramEmail);
    }
    const paramCreditApplicationId = params.get('creditApplicationId');
    if (paramCreditApplicationId) {
      fetchDataUsingTemplateId(paramCreditApplicationId);
    }
    const paramDomainId = params.get('domainId');
    if (paramDomainId) {
      fetchDataUsingDomainId(paramDomainId);
    }
  }, []);

  useEffect(() => {
    if (creditApplication) {
      const foundEmails: string[] = creditApplication.fieldValues
        .filter((field) => field.id.includes('trade-reference-company-email'))
        .map((field) => field.value.toLowerCase().trim());

      if (!foundEmails.includes(email.toLowerCase().trim())) {
        setModalState(ModalState.SUCCESS_SENDING_REFERENCE);
        setModalDetails({
          modalType: 'Error',
          title: 'Error',
          details: 'You have not been invited to see this credit application.',
        });
      }
      const matchingEmailFields = creditApplication.fieldValues.filter(
        (field) =>
          field.id.includes('trade-reference-company-email') &&
          field.value.toLowerCase().trim() === email.toLowerCase().trim()
      );
      // The same email can be listed on more than one reference; `pos` in the link picks the one
      // this request was for. Links sent before `pos` was added fall back to the first match.
      const pos = new URLSearchParams(location.search).get('pos');
      const foundEmail =
        matchingEmailFields.find(
          (field) => `${field.groupedPosition}**${field.groupStepPosition}` === pos
        ) ?? matchingEmailFields[0];
      if (foundEmail) {
        setReferenceId(foundEmail.uniqueId);
        setReferencePos(`${foundEmail.groupedPosition}**${foundEmail.groupStepPosition}`);
      }
      const getFieldValue = (id: string, fallback = ''): string =>
        creditApplication.fieldValues.find((fv) => fv.id === id)?.value ?? fallback;
      const creditCompanyName = getFieldValue('customer-or-company-name').trim();
      const address = getFieldValue('company-address').trim();
      const city = getFieldValue('company-city').trim();
      const state = getFieldValue('company-state').trim();
      const zip = getFieldValue('company-zip').trim();
      setCompanyName(creditCompanyName);
      setCompanyAddress(
        `${address ? `${address}, ` : ''}${city ? `${city}, ` : ''}${state ? `${state} ` : ''} ${
          zip ? zip : ''
        }`
      );
    }
    if (email) {
      setReferenceInput((prev) => ({ ...prev, email }));
    }
  }, [creditApplication, email]);

  const title = useMemo(() => {
    // No template here to name an external form by, so it keeps the generic title.
    if (creditApplication && !isExternalFormApplicationType(creditApplication.applicationType)) {
      return `${getCreditApplicationTypeLabel(creditApplication.applicationType, {
        long: true,
      })} Reference Form`;
    }
    return 'Application Reference Form';
  }, [creditApplication]);

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    REQUIRED_FIELDS.forEach((field) => {
      if (!referenceInput[field as keyof typeof referenceInput]) {
        newErrors[field] = `This field is required`;
      }
      const emailRegex = /[\w.+-]+@[\w-]+\.[a-z]{1,}/gi;
      if (field === 'email' && !emailRegex.test(referenceInput.email)) {
        newErrors.email = 'Invalid email address';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validate()) {
      setSubmitEmailModal(true);
    } else {
      setHasErrors(true);
    }
  };

  const handleSubmitData = async (val: string) => {
    setSending(true);
    const data = {
      email: val,
      ...(referenceInput.openSince && { openSince: referenceInput.openSince }),
      ...(referenceInput.termsOfPayment && { termsOfPayment: referenceInput.termsOfPayment }),
      ...(referenceInput.amountOfPastDue && {
        amountOfPastDue: Number(referenceInput.amountOfPastDue.replace(/,/g, '')),
      }),
      ...(referenceInput.highCredit && {
        highCredit: Number(referenceInput.highCredit.replace(/,/g, '')),
      }),
      ...(referenceInput.creditLimit && {
        creditLimit: Number(referenceInput.creditLimit.replace(/,/g, '')),
      }),
      ...(referenceInput.averageDaysBeyondTerms && {
        averageDaysBeyondTerms: Number(referenceInput.averageDaysBeyondTerms),
      }),
      ...(referenceInput.totalBalance && {
        totalBalance: Number(referenceInput.totalBalance.replace(/,/g, '')),
      }),
      ...(referenceInput.currentDue && {
        currentDue: Number(referenceInput.currentDue.replace(/,/g, '')),
      }),
      supplierId: basicSupplier?.id ?? '',
      creditApplicationId: creditApplication?.id ?? '',
      ...(referenceInput.terms30 && { terms30: Number(referenceInput.terms30.replace(/,/g, '')) }),
      ...(referenceInput.terms60 && { terms60: Number(referenceInput.terms60.replace(/,/g, '')) }),
      ...(referenceInput.terms90 && { terms90: Number(referenceInput.terms90.replace(/,/g, '')) }),
      ...(referenceInput.terms120Plus && {
        terms120Plus: Number(referenceInput.terms120Plus.replace(/,/g, '')),
      }),
      ...(referenceInput.returnedChecks && { returnedChecks: referenceInput.returnedChecks }),
      liensFiled: referenceInput.liensFiled,
      badDebtWriteOffs: referenceInput.badDebtWriteOffs,
      ...(referenceInput.comments && { comments: referenceInput.comments }),
      referenceId,
      referencePos,
    };
    const response = await dispatch(createCreditApplicationReference({ data }));
    if (
      response.type === 'creditApplicationReferences/createCreditApplicationReference/fulfilled'
    ) {
      setSending(false);
      setModalState(ModalState.SUCCESS_SENDING_REFERENCE);
      setModalDetails({
        modalType: 'Success',
        title: 'Success',
        details: 'You have successfully sent the credit application reference.',
      });
    } else {
      setSending(false);
      openDillToast({
        type: 'ERROR',
        message: 'Failed to send credit application reference',
      });
    }
  };

  return (
    <div className="w-full GREY_100-BG min-h-screen flex flex-col md:flex-row">
      <AppSubmitEmailModal
        open={submitEmailModal}
        handleClose={() => setSubmitEmailModal(false)}
        onSubmit={handleSubmitData}
        title="Enter email to send reference receipt"
        value={email}
      />
      <div className="flex flex-col md:justify-between px-5 py-8 w-full md:w-[260px] GREY_50-BG border-b md:border-b-0 md:border-r GREY_200-BORDER md:h-screen md:sticky md:top-0 md:overflow-y-auto">
        {/* Supplier branding */}
        <div className="flex flex-col items-center gap-3 mb-6">
          {basicSupplier?.logo?.url ? (
            <div className="flex items-center justify-center w-full h-24 p-3 rounded-2xl WHITE-BG shadow-sm border GREY_100-BORDER">
              <img
                src={basicSupplier.logo.url}
                className="max-h-16 max-w-full object-contain"
                alt={basicSupplier.name}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center w-full h-20 p-4 rounded-2xl WHITE-BG shadow-sm border GREY_100-BORDER">
              <img className="w-7/12 object-contain" src={dillLogoWithText} alt="Dill" />
            </div>
          )}
          {basicSupplier?.name && (
            <div className="text-center">
              <p className="text-base font-bold GREY_900-CLR">{basicSupplier.name}</p>
            </div>
          )}
        </div>

        {/* Trust badges */}
        <div className="flex flex-col gap-3 mb-6">
          <TrustBadge
            icon={<ShieldCheck size={18} className="text-white" weight="fill" />}
            title="Secure"
            description="Your data is fully encrypted and protected."
          />
          <TrustBadge
            icon={<ClockClockwise size={18} className="text-white" weight="fill" />}
            title="Quick & Easy"
            description="Complete your application in minutes."
          />
          <TrustBadge
            icon={<IdentificationCard size={18} className="text-white" weight="fill" />}
            title="No Account Required"
            description="Apply without creating an account."
          />
        </div>

        {/* Support */}
        <SupportCard />
      </div>

      <div className="flex w-full flex-col">
        <div className="flex items-center justify-between px-6 md:px-10 py-4 WHITE-BG border-b GREY_200-BORDER shadow-sm">
          <h1 className="text-lg md:text-xl font-bold GREY_900-CLR truncate mr-4">
            {`${basicSupplier?.name ?? ''} ${title}`.trim()}
          </h1>
          <a
            href="https://usedill.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 hover:opacity-80 transition-opacity">
            <span className="text-xs GREY_500-CLR whitespace-nowrap">Powered by</span>
            <img className="h-7 md:h-9" src={dillLogoWithText} alt="Dill" />
          </a>
        </div>

        <div className="w-full flex items-center justify-center px-4 md:px-0 mb-2">
          {modalState === ModalState.SEND_REFERENCE && (
            <div className="bg-white p-4 md:p-6 md:h-[calc(100vh-100px)] overflow-y-auto rounded-lg w-full sm:w-10/12 md:w-7/12 shadow gap-4 flex flex-col mt-4">
              <div className="p-4 rounded-xl GREY_50-BG border GREY_200-BORDER flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                  <div>
                    <p className="text-xs GREY_500-CLR mb-0.5">Company Name</p>
                    <p className="text-sm font-semibold GREY_900-CLR">{companyName || '—'}</p>
                  </div>
                  {companyAddress && <div className="hidden sm:block w-px h-8 GREY_300-BG" />}
                  {companyAddress && (
                    <div>
                      <p className="text-xs GREY_500-CLR mb-0.5">Address</p>
                      <p className="text-sm font-semibold GREY_900-CLR">{companyAddress}</p>
                    </div>
                  )}
                </div>
                <div className="border-t GREY_200-BORDER pt-3">
                  <p className="text-xs GREY_500-CLR leading-relaxed">
                    This company has given us your name as a credit reference. Please provide the
                    information below and submit your response as soon as possible. All information
                    will be held strictly confidential.
                  </p>
                </div>
              </div>
              <div className="w-full gap-2 md:gap-4 flex">
                <div className="w-full">
                  <AppDatePicker
                    isRequired={REQUIRED_FIELDS.includes('openSince')}
                    label="Account Open Since (MM/YYYY)"
                    selectedDate={openSinceDate}
                    dateFormat="MM/yyyy"
                    showMonthYearPicker={true}
                    errorText={errors.openSince}
                    maximumDate={new Date()}
                    onDateSelected={(date) => {
                      setOpenSinceDate(date ?? null);
                      onTextChange('openSince', date ? moment(date).format('MM/YYYY') : '');
                    }}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    isRequired={REQUIRED_FIELDS.includes('termsOfPayment')}
                    id="termsOfPayment"
                    label="Terms of Payment"
                    value={referenceInput.termsOfPayment}
                    errorText={errors.termsOfPayment}
                    onTextChange={(text) => {
                      onTextChange('termsOfPayment', text);
                    }}
                  />
                </div>
              </div>

              <div className="w-full gap-2 md:gap-4 flex">
                <div className="w-full">
                  <AppInputField
                    isRequired={REQUIRED_FIELDS.includes('totalBalance')}
                    id="totalBalance"
                    label="Total Balance"
                    prefix="$"
                    value={referenceInput.totalBalance}
                    errorText={errors.totalBalance}
                    onTextChange={(text) => {
                      onTextChange('totalBalance', formatWithCommas(text));
                    }}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    isRequired={REQUIRED_FIELDS.includes('currentDue')}
                    id="currentDue"
                    label="Current Due"
                    prefix="$"
                    value={referenceInput.currentDue}
                    errorText={errors.currentDue}
                    onTextChange={(text) => {
                      onTextChange('currentDue', formatWithCommas(text));
                    }}
                  />
                </div>
              </div>
              <div className="w-full gap-2 md:gap-4 flex">
                <div className="w-full">
                  <AppInputField
                    isRequired={REQUIRED_FIELDS.includes('amountOfPastDue')}
                    id="amountOfPastDue"
                    label="Amount Of Past Due"
                    prefix="$"
                    value={referenceInput.amountOfPastDue}
                    errorText={errors.amountOfPastDue}
                    onTextChange={(text) => {
                      onTextChange('amountOfPastDue', formatWithCommas(text));
                    }}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    isRequired={REQUIRED_FIELDS.includes('highCredit')}
                    id="highCredit"
                    label="High Credit"
                    prefix="$"
                    value={referenceInput.highCredit}
                    errorText={errors.highCredit}
                    onTextChange={(text) => {
                      onTextChange('highCredit', formatWithCommas(text));
                    }}
                  />
                </div>
              </div>
              <div className="w-full gap-2 md:gap-4 flex">
                <div className="w-full">
                  <AppInputField
                    isRequired={REQUIRED_FIELDS.includes('creditLimit')}
                    id="creditLimit"
                    label="Credit Limit"
                    prefix="$"
                    value={referenceInput.creditLimit}
                    errorText={errors.creditLimit}
                    onTextChange={(text) => {
                      onTextChange('creditLimit', formatWithCommas(text, 0));
                    }}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    isRequired={REQUIRED_FIELDS.includes('averageDaysBeyondTerms')}
                    id="averageDaysBeyondTerms"
                    label="Average Days Beyond Terms"
                    value={referenceInput.averageDaysBeyondTerms}
                    errorText={errors.averageDaysBeyondTerms}
                    onTextChange={(text) => {
                      onTextChange('averageDaysBeyondTerms', formatWithCommas(text, 0));
                    }}
                  />
                </div>
              </div>

              <div className="w-full gap-2 md:gap-4 flex items-center">
                <div className="w-full text-xs font-semibold">Days beyond Terms</div>
                <div className="w-full">
                  <AppInputField
                    id="terms30"
                    label="30 Days"
                    prefix="$"
                    value={referenceInput.terms30}
                    onTextChange={(text) => onTextChange('terms30', formatWithCommas(text))}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    id="terms60"
                    label="60 Days"
                    prefix="$"
                    value={referenceInput.terms60}
                    onTextChange={(text) => onTextChange('terms60', formatWithCommas(text))}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    id="terms90"
                    label="90 Days"
                    prefix="$"
                    value={referenceInput.terms90}
                    onTextChange={(text) => onTextChange('terms90', formatWithCommas(text))}
                  />
                </div>
                <div className="w-full">
                  <AppInputField
                    id="terms120Plus"
                    label="120+ Days"
                    prefix="$"
                    value={referenceInput.terms120Plus}
                    onTextChange={(text) => onTextChange('terms120Plus', formatWithCommas(text))}
                  />
                </div>
              </div>

              <div className="w-full gap-2 md:gap-4 flex">
                <div className="w-full">
                  <div className="text-xs mb-0.5 text-left  whitespace-nowrap font-medium">
                    Returned Checks?
                  </div>
                  <div className="flex gap-2 md:gap-4">
                    <div className="flex items-center">
                      <div>
                        <AppRadioButton
                          isChecked={referenceInput.returnedChecks === 'Yes'}
                          onClick={() => {
                            onTextChange('returnedChecks', 'Yes');
                          }}
                        />
                      </div>
                      <div className="mx-2 text-xs">Yes</div>
                    </div>
                    <div className="flex items-center">
                      <div>
                        <AppRadioButton
                          isChecked={referenceInput.returnedChecks === 'No'}
                          onClick={() => {
                            onTextChange('returnedChecks', 'No');
                          }}
                        />
                      </div>
                      <div className="mx-2 text-xs">No</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full gap-2 md:gap-4 flex">
                <div className="w-full">
                  <div className="text-xs mb-0.5 text-left  whitespace-nowrap font-medium">
                    Liens Filed?<span className="text-red-500">*</span>
                  </div>
                  <div className="flex gap-2 md:gap-4">
                    <div className="flex items-center">
                      <div>
                        <AppRadioButton
                          isChecked={referenceInput.liensFiled === 'Yes'}
                          onClick={() => {
                            onTextChange('liensFiled', 'Yes');
                          }}
                        />
                      </div>
                      <div className="mx-2 text-xs">Yes</div>
                    </div>
                    <div className="flex items-center">
                      <div>
                        <AppRadioButton
                          isChecked={referenceInput.liensFiled === 'No'}
                          onClick={() => {
                            onTextChange('liensFiled', 'No');
                          }}
                        />
                      </div>
                      <div className="mx-2 text-xs">No</div>
                    </div>
                  </div>
                  {errors.liensFiled && <p className="text-red-500 text-xs">{errors.liensFiled}</p>}
                </div>
                <div className="w-full">
                  <div className="text-xs mb-0.5 text-left  whitespace-nowrap font-medium">
                    Bad Debt Write-offs?<span className="text-red-500">*</span>
                  </div>
                  <div className="flex gap-2 md:gap-4">
                    <div className="flex items-center">
                      <div>
                        <AppRadioButton
                          isChecked={referenceInput.badDebtWriteOffs === 'Yes'}
                          onClick={() => {
                            onTextChange('badDebtWriteOffs', 'Yes');
                          }}
                        />
                      </div>
                      <div className="mx-2 text-xs">Yes</div>
                    </div>
                    <div className="flex items-center">
                      <div>
                        <AppRadioButton
                          isChecked={referenceInput.badDebtWriteOffs === 'No'}
                          onClick={() => {
                            onTextChange('badDebtWriteOffs', 'No');
                          }}
                        />
                      </div>
                      <div className="mx-2 text-xs">No</div>
                    </div>
                  </div>
                  {errors.badDebtWriteOffs && (
                    <p className="text-red-500 text-xs">{errors.badDebtWriteOffs}</p>
                  )}
                </div>
              </div>
              <div className="w-full gap-2 md:gap-4 flex">
                <div className="w-full">
                  <AppInputField
                    id="comments"
                    label="Comments"
                    inputType="textarea"
                    rows={4}
                    onTextChange={(text) => onTextChange('comments', text)}
                    placeholder="E.g. disputes"
                  />
                </div>
              </div>
              {hasErrors && (
                <div className="w-full">
                  <p className="text-red-500 text-sm text-center">
                    Please fix the errors above to continue.
                  </p>
                </div>
              )}
              <div className="w-full gap-2 md:gap-4 flex items-center justify-center">
                <DillButton
                  label="Submit"
                  onClick={handleSubmit}
                  isDisabled={sending}
                  isLoading={sending}
                  width={120}
                />
              </div>
            </div>
          )}
          {modalState === ModalState.SUCCESS_SENDING_REFERENCE && (
            <div className="flex flex-col items-center w-full justify-center">
              <div className="w-[calc(100%-30px)] md:w-[512px]">
                <JobSavingSuccess
                  type={modalDetails.modalType}
                  details={modalDetails.details}
                  title={modalDetails.title}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
